import React from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseIcon from '@material-ui/icons/Close';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles } from '@material-ui/core/styles';
import { SEVERITY_COLORS, SEVERITY_ICONS } from './severity';
import { Callout } from './shared';
import { resolveAttentionRecords } from '../attentionRecords';
import { destinationForItem, recordActionLabel, routeForRecord } from '../attentionTargets';
import { formatCount } from '../utils';

const MAX_VISIBLE = 50;

const useStyles = makeStyles(theme => ({
  paper: { width: 480, maxWidth: '94vw', display: 'flex', flexDirection: 'column', background: 'var(--contentColor, #fff)', color: 'var(--dm-text-strong, #263548)' },
  header: { padding: theme.spacing(2, 2, 2, 2.5), borderBottom: '1px solid var(--dm-border, #e7ebf0)' },
  titleRow: { display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1.25) },
  title: { flex: 1, minWidth: 0, fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 },
  detail: { color: 'var(--dm-muted, #647184)', fontSize: '.8rem', lineHeight: 1.55, marginTop: theme.spacing(1) },
  content: { flex: 1, overflowY: 'auto', padding: theme.spacing(2, 2.5) },
  listTitle: { fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--dm-muted, #647184)', marginBottom: theme.spacing(1) },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: theme.spacing(1) },
  row: {
    width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5), borderRadius: 12, border: '1px solid var(--dm-border, #e7ebf0)', textAlign: 'left',
    background: 'var(--dm-row-alt, #f8fafc)', transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
    '&:hover, &.Mui-focusVisible': { borderColor: theme.palette.primary.main, background: 'rgba(61, 125, 221, .06)' },
  },
  rowLabel: { fontSize: '.84rem', fontWeight: 700, overflowWrap: 'anywhere' },
  rowSub: { color: 'var(--dm-muted, #647184)', fontSize: '.75rem', lineHeight: 1.45, marginTop: 2, overflowWrap: 'anywhere' },
  rowAction: { display: 'inline-flex', alignItems: 'center', color: theme.palette.primary.main, fontSize: '.75rem', fontWeight: 700, whiteSpace: 'nowrap' },
  empty: { padding: theme.spacing(2), borderRadius: 12, border: '1px dashed var(--dm-border, #d5dde8)', color: 'var(--dm-muted, #647184)', fontSize: '.82rem', lineHeight: 1.55 },
  footer: { padding: theme.spacing(1.5, 2.5), borderTop: '1px solid var(--dm-border, #e7ebf0)', display: 'flex', justifyContent: 'flex-end' },
}));

const AttentionDrawer = ({ item, data, service, onClose, onNavigate }) => {
  const classes = useStyles();
  const [state, setState] = React.useState({ loading: false, records: [], source: 'none', error: '' });
  const requestId = React.useRef(0);

  const load = React.useCallback(() => {
    if (!item) return;
    const current = ++requestId.current;
    setState({ loading: true, records: [], source: 'none', error: '' });
    resolveAttentionRecords(item, data, service)
      .then(result => { if (current === requestId.current) setState({ loading: false, error: '', ...result }); })
      .catch(() => { if (current === requestId.current) setState({ loading: false, records: [], source: 'none', error: 'We could not load the affected records.' }); });
  }, [item, data, service]);

  React.useEffect(() => {
    load();
    return () => { requestId.current += 1; };
  }, [load]);

  const color = SEVERITY_COLORS[item?.severity] || SEVERITY_COLORS.info;
  const Icon = SEVERITY_ICONS[item?.severity] || SEVERITY_ICONS.info;
  const destination = destinationForItem(item);
  const visible = state.records.slice(0, MAX_VISIBLE);
  const countDiffers = state.source === 'derived' && Number(item?.count) !== state.records.length;

  return (
    <Drawer anchor="right" open={!!item} onClose={onClose} classes={{ paper: classes.paper }} aria-labelledby="attention-drawer-title">
      {item ? <>
        <div className={classes.header}>
          <div className={classes.titleRow}>
            <Icon style={{ color, marginTop: 2 }} aria-hidden="true" />
            <Typography id="attention-drawer-title" component="h2" className={classes.title}>{item.title}</Typography>
            <IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </div>
          {item.detail ? <Typography className={classes.detail}>{item.detail}</Typography> : null}
        </div>
        <div className={classes.content} aria-busy={state.loading}>
          {state.loading ? (
            <div className={classes.list}>{[0, 1, 2].map(index => <Skeleton key={index} variant="rect" height={58} style={{ borderRadius: 12 }} />)}</div>
          ) : state.error ? (
            <div className={classes.empty} role="alert">{state.error} <Button size="small" color="primary" onClick={load}>Try again</Button></div>
          ) : visible.length ? <>
            {countDiffers ? <Callout info title={`Found ${formatCount(state.records.length)} matching ${state.records.length === 1 ? 'record' : 'records'}`} body={`The summary counts ${formatCount(item.count)}. This list is worked out from other dashboard data until the API returns the exact records.`} /> : null}
            <Typography component="h3" className={classes.listTitle}>Affected ({formatCount(state.records.length)})</Typography>
            <ul className={classes.list}>
              {visible.map((record, index) => {
                const route = routeForRecord(item, record);
                return (
                  <li key={record.id || index}>
                    <ButtonBase className={classes.row} disabled={!route} onClick={() => onNavigate(route)} aria-label={`${recordActionLabel(item, record)}: ${record.label}`}>
                      <span style={{ minWidth: 0 }}>
                        <Typography component="span" display="block" className={classes.rowLabel}>{record.label}</Typography>
                        {record.sublabel ? <Typography component="span" display="block" className={classes.rowSub}>{record.sublabel}</Typography> : null}
                      </span>
                      {route ? <span className={classes.rowAction}>{recordActionLabel(item, record)}<ChevronRightIcon fontSize="small" /></span> : null}
                    </ButtonBase>
                  </li>
                );
              })}
            </ul>
            {state.records.length > MAX_VISIBLE ? <Typography className={classes.detail}>Showing the first {MAX_VISIBLE}. Use the full list for the rest.</Typography> : null}
          </> : (
            <div className={classes.empty}>
              {state.source === 'none'
                ? `The dashboard only receives the total for this item (${formatCount(item.count)}), not which records they are, so it can't list them here yet.`
                : 'No matching records were found in the related data.'}
            </div>
          )}
        </div>
        {destination.route ? (
          <div className={classes.footer}>
            <Button variant="outlined" color="primary" onClick={() => onNavigate(destination.route)}>{destination.label}</Button>
          </div>
        ) : null}
      </> : null}
    </Drawer>
  );
};

export default AttentionDrawer;
